import React from 'react';
import { ExpressionLogic, TOKEN_RE } from '@/modules/form/lang';
import {
  OTHER_OPTION,
  TemplateLogic,
  type ContextVars,
  type Template,
} from '@/modules/template';

// GARDEN-CUTOVER(#658): the clickable-token preview of a classic template,
// which reads its fields and primitives through `TemplateLogic`. Delete this
// file with classic templates.

// Extracted verbatim from ComposeWithTemplate.tsx (#447) so news steps can
// reuse the clickable-token preview engine.

/**
 * The clickable-token preview of a message being composed.
 *
 * `singletonMode` — whether each message reaches exactly one person — is what
 * the field editor gates its option chips on, and the preview must agree: a
 * field offering nothing in this mode (an optional slot restricted to `dm` in a group
 * text) is dropped here too, rather than shown as a token that leads to an
 * empty picker.
 */
export function renderPreviewMessage(
  template: Template,
  fieldValues: Readonly<Record<string, string>>,
  context: ContextVars,
  activeFieldId: string | null,
  onFieldSelect: (fieldId: string) => void,
  singletonMode: boolean
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const fieldsById = new Map(template.fields.map(field => [field.id, field]));
  let lastIndex = 0;
  const resolve = TemplateLogic.resolver(template, fieldValues, context);
  const tokenRe = new RegExp(TOKEN_RE);
  let match: RegExpExecArray | null;

  while ((match = tokenRe.exec(template.body)) !== null) {
    const token = match[1];
    const start = match.index;

    if (start > lastIndex) {
      parts.push(template.body.slice(lastIndex, start));
    }

    const field = fieldsById.get(token);
    if (!field) {
      // A primitive, or a call: printed as it composes, not clickable.
      parts.push(ExpressionLogic.evaluateSource(token, resolve) ?? '');
    } else if (
      field &&
      TemplateLogic.isVestigialField(field, context, singletonMode)
    ) {
      // Nothing this field offers here can add to the message — drop the token
      // rather than offer an empty choice.
      lastIndex = start + match[0].length;
      continue;
    } else {
      const value = fieldValues[token] ?? '';
      const hasValue = value.trim().length > 0;
      const literalOptions = (
        field ? TemplateLogic.allOptions(field) : []
      ).filter(option => option !== OTHER_OPTION);
      const selectedOption = hasValue
        ? literalOptions.find(option => option === value)
        : undefined;
      // A primitive option (e.g. `{_profileName}`) can resolve to an empty
      // string (no profile name set).  In that case the token falls back to the
      // field's label, so it stays visible and clickable.
      const resolvedOption = selectedOption
        ? TemplateLogic.optionText(field, selectedOption, context)
        : null;
      const hasResolvedValue =
        resolvedOption != null && resolvedOption.trim().length > 0;
      const kind = hasResolvedValue
        ? 'preset'
        : hasValue && resolvedOption === null
          ? 'user'
          : 'missing';
      const renderedValue = hasResolvedValue
        ? (resolvedOption ?? '')
        : hasValue && resolvedOption === null
          ? value
          : field
            ? TemplateLogic.fieldLabel(field, context)
            : '';

      parts.push(
        <PreviewToken
          key={`${start}-${token}`}
          active={token === activeFieldId}
          kind={kind}
          fieldId={token}
          onSelect={() => onFieldSelect(token)}
        >
          {renderedValue}
        </PreviewToken>
      );
    }

    lastIndex = start + match[0].length;
  }

  if (lastIndex < template.body.length) {
    parts.push(template.body.slice(lastIndex));
  }

  return parts;
}

export const PreviewToken: React.FC<{
  active: boolean;
  children: string;
  fieldId: string;
  kind: 'missing' | 'preset' | 'user';
  onSelect: () => void;
}> = ({ active, children, fieldId, kind, onSelect }) => (
  <button
    type="button"
    className={`section-card__message-preview-token section-card__message-preview-token--${kind} ${
      active ? 'section-card__message-preview-token--active' : ''
    }`}
    data-active={active ? 'true' : 'false'}
    data-field-id={fieldId}
    onClick={onSelect}
  >
    {children}
  </button>
);
// END GARDEN-CUTOVER(#658)
